import { css } from '@emotion/react';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Stack, Typography } from '@mui/material';
import {
  GetRowIdParams,
  GridPreDestroyedEvent,
  GridReadyEvent,
  RowClassParams,
  RowSelectionOptions,
  RowStyle,
} from 'ag-grid-community';
import { motion } from 'motion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useState } from 'react';
import { BlockerFunction } from 'react-router';
import { AgTableWithBaseStyle } from '../../../components/AgTable/AgTable2';
import { multiRowSelectionDefs } from '../../../components/AgTable/rowSelectionDefs';
import { FormLoadingButton } from '../../../components/Form/FormComponents';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { companyMappingResponseToVM, CompanyMappingVM } from '../../../schemas/CompanyMappings.schema';
import {
  getCompanyMappings,
  resolveNonInvestranMappings,
} from '../../../services/queries/MaggieCompanyQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { disabledCheckboxCss, editableGridCss } from '../../../theme/gridStyle';
import { NavigationBlocker } from '../../CompanyProfiles/Scenarios/SingleScenario/block-navigation/NavigationBlocker';
import {
  collectModifiedMappings,
  isSelectableMapping,
  mappingsGridApiAtom,
  showCreateCompanyDialogState,
  useHasDirtyMappings,
  useMappingsGridEditState,
  useModifiedMappingsCount,
} from './CompanyMappingsState';
import { CreateMappingCompanyDialog } from './MappingsCompanyField';
import { defaultColDefs, useCompanyMappingsColDefs } from './useCompanyMappingsColDefs';
import { useCompanyMappingsData } from './useCompanyMappingsData';
import { selectedMappings, useMappingsRowSelection } from './useMappingsRowSelection';
import { useUpdateCompanyMappings } from './useUpdateCompanyMappings';

interface CompanyMappingsProps {
  filterCompanyName?: string;
}

export function CompanyMappings({ filterCompanyName }: CompanyMappingsProps) {
  const showCreateCompanyDialog = useAtomValue(showCreateCompanyDialogState);
  const gridApi = useAtomValue(mappingsGridApiAtom);
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();
  const [resolving, setResolving] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const hasDirtyMappings = useHasDirtyMappings();
  const modifiedCount = useModifiedMappingsCount();
  const { createMappings } = useUpdateCompanyMappings();
  const shouldBlockNavigation = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      hasDirtyMappings && currentLocation.pathname !== nextLocation.pathname,
    [hasDirtyMappings]
  );

  const handleResolveWithAI = useCallback(async () => {
    setResolving(true);
    try {
      const result = await resolveNonInvestranMappings();
      const fresh = await getCompanyMappings();
      gridApi?.applyTransaction({ update: fresh.map(companyMappingResponseToVM) });
      pushSuccessToast({ message: `Resolved ${result.resolved} of ${result.total} companies via AI` });
    } catch (err) {
      pushErrorToast({ message: getErrorMessage(err, 'Failed to resolve mappings') });
    } finally {
      setResolving(false);
    }
  }, [gridApi, pushErrorToast, pushSuccessToast]);

  const handleSaveAll = useCallback(async () => {
    const rows = collectModifiedMappings(gridApi);
    if (rows.length === 0) {
      return;
    }
    setSavingAll(true);
    try {
      const saved = await createMappings(rows);
      if (saved) {
        pushSuccessToast({ message: `Saved ${rows.length} mapping${rows.length === 1 ? '' : 's'}` });
      }
    } finally {
      setSavingAll(false);
    }
  }, [createMappings, gridApi, pushSuccessToast]);

  return (
    <>
      {showCreateCompanyDialog && <CreateMappingCompanyDialog />}
      <NavigationBlocker shouldBlock={shouldBlockNavigation} />
      <Stack padding='8px 12px 12px' height='100%' width='100%' display='grid' gridTemplateRows='auto 1fr'>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h4'>Mappings</Typography>
          <Stack direction='row' gap='0.75rem'>
            <FormLoadingButton loading={resolving} onClick={handleResolveWithAI}>
              Resolve via AI
            </FormLoadingButton>
            <LoadingButton
              variant='contained'
              color='secondary'
              loading={savingAll}
              disabled={modifiedCount === 0}
              onClick={handleSaveAll}
            >
              {modifiedCount > 0 ? `Save All (${modifiedCount})` : 'Save All'}
            </LoadingButton>
          </Stack>
        </Stack>
        <Stack pt='1rem' height='100%' css={[editableGridCss, disabledCheckboxCss]}>
          <CompanyMappingsGrid filterCompanyName={filterCompanyName} />
        </Stack>
      </Stack>
    </>
  );
}

interface CompanyMappingsGridProps {
  filterCompanyName?: string;
}

function CompanyMappingsGrid({ filterCompanyName }: CompanyMappingsGridProps) {
  const { data: allData, error } = useCompanyMappingsData();

  // Filter data if company name is provided
  const data = React.useMemo(() => {
    if (!allData) {
      return allData;
    }
    if (!filterCompanyName) {
      return allData;
    }

    const filtered = allData.filter((item) => item.providerCompanyName === filterCompanyName);
    return filtered;
  }, [allData, filterCompanyName]);
  const colDefs = useCompanyMappingsColDefs();
  const setMappingsGridApi = useSetAtom(mappingsGridApiAtom);
  const { onSelectionChanged, deselectAll } = useMappingsRowSelection();
  const { onCellEditingStarted, onCellValueChanged } = useMappingsGridEditState();

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      setMappingsGridApi(e.api);
      e.api.addEventListener('cellEditingStarted', onCellEditingStarted);
      e.api.addEventListener('cellValueChanged', onCellValueChanged);
    },
    [setMappingsGridApi, onCellEditingStarted, onCellValueChanged]
  );

  const onGridPreDestroyed = useCallback(
    (e: GridPreDestroyedEvent) => {
      e.api.removeEventListener('cellEditingStarted', onCellEditingStarted);
      e.api.removeEventListener('cellValueChanged', onCellValueChanged);
      setMappingsGridApi(null);
    },
    [onCellEditingStarted, onCellValueChanged, setMappingsGridApi]
  );

  const getRowId = useCallback((params: GetRowIdParams) => params.data?.id ?? '', []);

  if (error) {
    return <Alert severity='error'>{error}</Alert>;
  }
  return (
    <>
      <MappingsActions onCancel={deselectAll} />
      <AgTableWithBaseStyle
        columnDefs={colDefs}
        defaultColDef={defaultColDefs}
        groupDefaultExpanded={-1}
        onGridReady={onGridReady}
        onSelectionChanged={onSelectionChanged}
        rowData={data}
        rowSelection={rowSelection}
        rowGroupPanelShow='always'
        onGridPreDestroyed={onGridPreDestroyed}
        getRowId={getRowId}
        getRowStyle={getDirtyRowStyle}
      />
    </>
  );
}

const ActionsContainerCss = css`
  height: 0;
  overflow: visible;
  z-index: 3;
  transform: translateY(-100%);
  & > * {
    margin: 0.75rem;
    height: 1.75rem;
  }
`;
interface IMappingsActionsProps {
  onCancel: () => void;
}
function MappingsActions(props: IMappingsActionsProps) {
  const selected = useAtomValue(selectedMappings);
  const { createMappings } = useUpdateCompanyMappings();
  const { onCancel } = props;

  const [loading, setLoading] = useState(false);
  const handleSave = useCallback(async () => {
    setLoading(true);
    await createMappings(selected);
    setLoading(false);
  }, [selected, createMappings]);

  if (selected.length === 0) {
    return null;
  }

  return (
    <motion.div css={ActionsContainerCss} animate={{ opacity: [0, 1] }} transition={{ duration: 0.5 }}>
      <Stack direction='row' justifyContent='end' gap='1rem'>
        <FormLoadingButton onClick={handleSave} loading={loading}>
          Save Selected
        </FormLoadingButton>
        <Button variant='outlined' color='secondary' onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </motion.div>
  );
}

const rowSelection: RowSelectionOptions = {
  ...multiRowSelectionDefs,
  isRowSelectable: (node) => isSelectableMapping(node.data),
};

const DIRTY_ROW_BG = 'rgba(255, 193, 7, 0.18)';
function getDirtyRowStyle(params: RowClassParams<CompanyMappingVM>): RowStyle | undefined {
  return params.data?.modified ? { backgroundColor: DIRTY_ROW_BG } : undefined;
}
